import {
  boundsOfValue,
  type Evaluated,
  isReshapeable,
  parentsOf,
  type Reshape,
  reshapeSource,
  type Similarity,
  transformSource,
  translation,
} from '@/domain/construction';
import { bounds as boundsOps, type Bounds } from '@/domain/geometry';
import { type Vec2, vec } from '@/domain/math';
import { type EntityId, type SceneDocument } from '@/domain/model';

import { type EditAction } from './actions';

/**
 * Moving, turning and resizing what the user has selected.
 *
 * ## The closure, and why it goes *upwards*
 *
 * Selecting a triangle usually means selecting three segments. The segments have no
 * coordinates — they are derived from corner points that the user did not select and probably
 * does not think of as separate things. Transforming only what was selected would therefore
 * do nothing at all, which is exactly how a naive implementation of this feature fails.
 *
 * So the selection is closed over its **ancestors**: everything it transitively depends on.
 * From that closure, the entities that carry coordinates of their own are the ones rewritten,
 * and evaluation carries the rest. The three corner points move; the segments follow; the
 * circumcircle drawn round them stays the circumcircle.
 *
 * ## What this deliberately does not do
 *
 * It does not close over *descendants*. Selecting one corner of a triangle and dragging the
 * selection moves that corner alone — which is the same thing dragging the point does, and
 * has to stay that way, or single-point editing would stop working the moment a point was
 * also selected.
 *
 * It also does not detach anything. If the selection depends on a point that something
 * *outside* the selection also depends on, that outside thing moves too. The alternative —
 * silently duplicating the shared point — would break the construction the user built on
 * purpose. Sharing a vertex is a statement, and the editor keeps it.
 */

/**
 * The entities a transform of `selection` would actually rewrite.
 *
 * Exposed separately from `transformActions` because the interface needs to know whether a
 * gesture will do anything *before* offering it: a selection whose ancestors are all derived
 * cannot be moved, and a drag handle that does nothing is worse than one that is absent.
 */
export function movableRootsOf(
  document: SceneDocument,
  selection: Iterable<EntityId>
): readonly EntityId[] {
  const seen = new Set<EntityId>();
  const queue = [...selection];

  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined) break;
    if (seen.has(current)) continue;
    seen.add(current);

    const entity = document.entities.get(current);
    if (entity === undefined) continue;
    queue.push(...parentsOf(entity.source));
  }

  const roots: EntityId[] = [];
  for (const id of seen) {
    const entity = document.entities.get(id);
    if (entity === undefined) continue;
    if (entity.locked) continue;
    // A source that transforms to itself is derived; only the ones that change are rewritten.
    if (transformSource(entity.source, translation(vec.vec2(1, 1))) !== null) roots.push(id);
  }
  return roots;
}

/**
 * The edits that apply `transform` to `selection`.
 *
 * Returns an empty list when there is nothing to move, so the caller can treat "no actions"
 * as "this gesture is not available" without a second query.
 */
export function transformActions(
  document: SceneDocument,
  selection: Iterable<EntityId>,
  transform: Similarity
): readonly EditAction[] {
  const actions: EditAction[] = [];

  for (const id of movableRootsOf(document, selection)) {
    const entity = document.entities.get(id);
    if (entity === undefined) continue;

    const moved = transformSource(entity.source, transform);
    if (moved === null) continue;

    actions.push({ kind: 'update-entity', entity: { ...entity, source: moved } });
  }

  return actions;
}

/**
 * The box round a selection, for placing a rotation centre.
 *
 * Uses the *selected* entities rather than the closure: the user is thinking about what they
 * can see highlighted, and a centre that sat somewhere else — out at a construction point they
 * never selected — would make every rotation feel wrong.
 */
export function selectionBounds(
  values: ReadonlyMap<EntityId, Evaluated | null>,
  selection: Iterable<EntityId>
): Bounds | null {
  let box: Bounds | null = null;

  for (const id of selection) {
    const value = values.get(id) ?? null;
    if (value === null) continue;

    // Unbounded geometry contributes nothing: a line has no extent to centre a rotation on.
    const own = boundsOfValue(value);
    if (own === null) continue;
    box = box === null ? own : boundsOps.union(box, own);
  }

  return box;
}

/**
 * The one entity a per-axis reshape would act on, or `null` when there is not exactly one.
 *
 * ## Why exactly one
 *
 * `transformActions` may act on a whole selection because a similarity is closed over every kind
 * of source: whatever is in there, each can express its own image. A per-axis stretch is not —
 * a circle defined by two points cannot become an ellipse, and a shape with a stored rotation
 * can only be stretched along its own axes, which are not the axes of the shape beside it.
 *
 * So the gesture is offered only when the answer is unambiguous: one movable root, and one that
 * stores its axes separately. Two shapes with different orientations have no single frame to
 * stretch along, and picking one of them would silently shear the other.
 */
export function reshapableRootOf(
  document: SceneDocument,
  selection: Iterable<EntityId>
): EntityId | null {
  const roots = movableRootsOf(document, selection);
  if (roots.length !== 1) return null;

  const only = roots[0];
  if (only === undefined) return null;

  const entity = document.entities.get(only);
  if (entity === undefined || !isReshapeable(entity.source)) return null;

  return only;
}

/**
 * The edits that stretch `selection` along `reshape`'s axes.
 *
 * **All or nothing.** A source that cannot express the map refuses, and one refusal returns an
 * empty list rather than a shorter one. Skipping the refusals — which is what copying
 * `transformActions`' `continue` would do, because there `null` means "it follows by itself" —
 * would squash a figure in parts and report nothing.
 */
export function reshapeActions(
  document: SceneDocument,
  selection: Iterable<EntityId>,
  reshape: Reshape
): readonly EditAction[] {
  const actions: EditAction[] = [];

  for (const id of movableRootsOf(document, selection)) {
    const entity = document.entities.get(id);
    if (entity === undefined) continue;

    const result = reshapeSource(entity.source, reshape);
    if (result.kind === 'refused') return [];
    if (result.kind === 'follows') continue;

    actions.push({ kind: 'update-entity', entity: { ...entity, source: result.source } });
  }

  return actions;
}

/** The centre of a selection, or the origin when it has no extent to speak of. */
export function selectionCenter(
  values: ReadonlyMap<EntityId, Evaluated | null>,
  selection: Iterable<EntityId>
): Vec2 {
  const box = selectionBounds(values, selection);
  if (box === null) return vec.vec2(0, 0);
  return vec.vec2((box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2);
}
